// colors.ts — theme-aware color derivation for the git graph and the feed
// charts. Every color here comes out of lib/web's palette contrast solver
// (the `palette.solveContrastColor` idiom DESIGN.md blesses), so hues are
// derived but lightness/chroma are solved against the active surfaces
// rather than hand-picked per theme.
import { palette } from 'common';
import type { ThemeMode } from './theme';

// Hash a string to a 32-bit unsigned int. Used to seed deterministic hues
// for branch/author/metric color derivation.
export function hashStr(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

// Derive a hue (0-359) from a string deterministically.
export function hueOf(s: string): number {
  return hashStr(s) % 360;
}

// Solve an OKLCH color at the given hue that reads well against the active
// body bg AND a card surface — the same call shape authz uses for chip
// colors. Light vs dark is passed in so a theme toggle re-solves.
export function colorForHue(hue: number, mode: ThemeMode): string {
  const bg = mode === 'dark' ? { r: 0.135, g: 0.135, b: 0.135 } : { r: 0.97, g: 0.97, b: 0.97 };
  const surf = mode === 'dark' ? { r: 0.20, g: 0.20, b: 0.20 } : { r: 1.0, g: 1.0, b: 1.0 };
  const c = palette.solveContrastColor(hue, bg, surf, {
    minBgContrast: 3.5,
    minSurfaceContrast: 2.5,
    cMin: 0.12,
    cMax: 0.18,
  });
  return palette.oklchToHex(c);
}

// True if the given hex would read white-on-color better than black-on-color.
export function preferWhiteFg(hex: string): boolean {
  const ok = palette.hexToOklch(hex);
  if (!ok) return true;
  return ok.L < 0.65;
}

// Deterministic per-metric-name color. The 'metric:' prefix scopes the hue
// space away from branch/author hashes so metric values stand out.
export function metricColor(name: string, mode: ThemeMode): string {
  return colorForHue(hueOf('metric:' + name), mode);
}

// Categorical series slots for the feed charts. Hashed hues collide too
// easily when eight lines share one chart, so slots walk the hue circle in
// a fixed order (golden-angle-ish steps, 8 slots before it wraps) and each
// hue is solved against the active surfaces like every other color here.
// A feed keeps the slot it was given when it was selected, so deselecting
// one feed never repaints the survivors.
const SERIES_HUES = [250, 35, 150, 85, 330, 195, 290, 15];

export const SERIES_SLOTS = SERIES_HUES.length;

export function seriesColor(slot: number, mode: ThemeMode): string {
  const hue = SERIES_HUES[((slot % SERIES_SLOTS) + SERIES_SLOTS) % SERIES_SLOTS];
  return colorForHue(hue, mode);
}
